import { useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import { ExhibitSchemeDialog } from "../../../caseFiles/components/ExhibitSchemeDialog";
import {
  CUSTOM_STYLE_VALUE,
  ExhibitStyleSelectItems
} from "../../../caseFiles/components/ExhibitStyleSelectItems";
import {
  applyPreset,
  EXHIBIT_STYLE_PRESETS,
  matchPreset,
  presetSamples
} from "../../../caseFiles/exhibitStylePresets";
import type { ExhibitScheme } from "../../../caseFiles/types";
import { useCaseData } from "../../data/CaseDataContext";
import { useWorkflows } from "../context/WorkflowsContext";
import { labelStyleOf } from "../settings";

/**
 * The case's exhibit label style, set right on the card the way the Case
 * Files Exhibit Format menu sets it: it saves at once, with Undo in the toast.
 */
export const NumberStyleSelect: FC = () => {
  const w = useWorkflows();
  const { fs } = useCaseData();
  const current = matchPreset(fs.scheme);
  const [custom, setCustom] = useState(false);

  const apply = (next: ExhibitScheme) => {
    const previous = fs.scheme;
    void fs.saveScheme(next);
    toast.success(`Exhibits now number as ${labelStyleOf(next) ?? "set"}`, {
      action: {
        label: "Undo",
        onClick: () => void fs.saveScheme(previous)
      }
    });
  };

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
      {w.caseLabelStyle && w.firmScheme ? (
        <Button
          variant="ghost"
          size="xs"
          className="shrink-0"
          title={`The firm numbers in ${labelStyleOf(w.firmScheme)}`}
          onClick={() => w.firmScheme && apply(w.firmScheme)}
          data-testid="workflow-number-firm"
        >
          Use Firm Style
        </Button>
      ) : null}
      <Select
        value={current?.id ?? "custom"}
        onValueChange={(id) => {
          if (id === CUSTOM_STYLE_VALUE) {
            setCustom(true);
            return;
          }
          const preset = EXHIBIT_STYLE_PRESETS.find((p) => p.id === id);
          if (preset && preset.id !== current?.id)
            apply(applyPreset(fs.scheme, preset));
        }}
      >
        <SelectTrigger
          size="sm"
          className="max-w-56 min-w-0"
          aria-label="Exhibit Label Style"
          data-testid="workflow-number-style"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {current ? null : (
            <SelectItem value="custom" disabled>
              Custom Style
            </SelectItem>
          )}
          <ExhibitStyleSelectItems
            samples={(preset) => presetSamples(fs.scheme, preset, 2).join("  ")}
            testIdPrefix="workflow-number-style"
          />
        </SelectContent>
      </Select>
      <ExhibitSchemeDialog
        open={custom}
        onOpenChange={setCustom}
        scheme={fs.scheme}
        scope="case"
        onSave={apply}
      />
    </div>
  );
};
